import React from "react";
import { some, includes } from "lodash";
import Activity from "./Activity";
import Fieldset from "../Fieldset/Fieldset";
import Label from "../Label/Label";
import FormByPhase from "./FormByPhase";
import FormByTabs from "./FormByTabs";
import FormByAccordion from "./FormByAccordion";
import { isRequiredWithValue, isSubProcess } from "../../utilities";
import "./form-format.scss";

const DATA_TYPES = {
  web: "text",
  email: "text",
  int: "text",
  money: "text",
  image: "file",
  bool: "checkbox",
  html: "richtext",
};

const ACCEPTED_FILES = {
  file: "/*",
  image: "image/*",
};

const ACTIVITY_WIDTH = {
  small: "small-activity",
  medium: "medium-activity",
  large: "large-activity",
};

export const getActivities = (props) => {
  const {
    isHeaderShow = true,
    touched,
    errors,
    description,
    elements,
    phaseNo,
    onActivityBlur,
    onActivityChange,
    onUpload,
    onKeyDown,
    ...rest
  } = props;

  const isPhaseVisible = (element, phaseNo) => {
    return (
      element.visible &&
      includes(
        element.phases.map(({ id }) => id),
        phaseNo
      )
    );
  };

  const getNestingActivities = ({
    touched,
    errors,
    element,
    phaseNo,
    onActivityChange,
    onActivityBlur,
    onUpload,
    onRemove,
    onKeyDown,
  }) => {
    if (!isPhaseVisible(element, phaseNo)) return null;
    return (
      <Fieldset
        key={element.activityId}
        title={element.label}
        pending={element.pending}
        element={element}
      >
        {element.elements.map((element) => {
          return isSubProcess(element.type)
            ? getNestingActivities({
                touched,
                errors,
                element,
                phaseNo,
                onActivityChange,
                onActivityBlur,
                onUpload,
                onRemove,
                onKeyDown,
              })
            : isPhaseVisible(element, phaseNo) && (
                <Activity
                  key={element.activityId}
                  error={
                    (touched[element.id] || touched.formating) &&
                    errors[element.id]
                  }
                  isRequired={isRequiredWithValue(element)}
                  {...element}
                  className={ACTIVITY_WIDTH[element.width]}
                  width={!ACTIVITY_WIDTH[element.width] ? element.width + "%" : ""}
                  type={
                    DATA_TYPES[element.presentationType] ||
                    element.presentationType
                  }
                  accept={ACCEPTED_FILES[element.presentationType]}
                  disabled={element.readOnly || !element.enabled}
                  hintText={element.hinttext}
                  activityId={element.activityId}
                  onChange={(e) => {
                    onActivityChange(e, element);
                  }}
                  onBlur={(e) => onActivityBlur(e, element)}
                  onUpload={onUpload}
                  onRemove={onRemove}
                  onKeyDown={(e) => onKeyDown(e, element)}
                />
              );
        })}
      </Fieldset>
    );
  };

  return (
    <>
      {isHeaderShow && <Label className="phase__header">{description}</Label>}
      {elements.map((element) => {
        if (isSubProcess(element.type)) {
          return getNestingActivities({
            touched,
            errors,
            element,
            phaseNo,
            onActivityChange,
            onActivityBlur,
            onUpload,
            onKeyDown,
          });
        }

        return (
          <Activity
            key={element.activityId}
            error={
              (touched[element.id] || touched.formating) && errors[element.id]
            }
            isRequired={isRequiredWithValue(element)}
            {...element}
            className={ACTIVITY_WIDTH[element.width]}
            width={!ACTIVITY_WIDTH[element.width] ? element.width + "%" : ""}
            accept={ACCEPTED_FILES[element.presentationType]}
            disabled={element.readOnly || !element.enabled}
            hintText={element.hinttext}
            activityId={element.activityId}
            onChange={(e) => {
              onActivityChange(e, element);
            }}
            onBlur={(e) => onActivityBlur(e, element)}
            onKeyDown={(e) => onKeyDown(e, element)}
            onUpload={onUpload}
            {...rest}
            type={
              DATA_TYPES[element.presentationType] || element.presentationType
            }
          />
        );
      })}
    </>
  );
};

export const getPhaseTitle = (title, activities, showPendingCount, isPhase) => {
  if (!showPendingCount) return title;
  const pendingActivities = activities.filter(
    (activity) => activity.visible && activity.pending && !activity.value
  );

  if (pendingActivities.length) {
    if (isPhase) {
      return (
        <>
          <div>{title}</div>
          <div className="pending-events">({pendingActivities.length})</div>
        </>
      );
    } else {
      return title + ` (${pendingActivities.length})`;
    }
  }

  return title;
};

export const isPhaseValidate = (elements, touched, errors) => {
  return some(elements, (item) => {
    return (
      item.pending &&
      (touched[item.id] || touched.formating) &&
      Object.keys(errors).includes(item.id)
    );
  });
};

const isOnlyUnassignedPhase = (length, title) => {
  return length === 1 && title === "Unassigned";
};

const FormFormat = (props) => {
  const { type, touched, errors, phases, formShowPendingCount } = props;
  const FORM_TYPE = {
    VIEW_BY_PHASE: "Phases",
    TABS: "Tabs",
    ACCORDION: "Accordion",
  };

  if (type === FORM_TYPE.VIEW_BY_PHASE) {
    return <FormByPhase {...props} />;
  }

  if (type === FORM_TYPE.TABS) {
    return <FormByTabs {...props} />;
  }

  if (type === FORM_TYPE.ACCORDION) {
    return <FormByAccordion {...props} />;
  }

  if (phases && phases.length > 0 && isOnlyUnassignedPhase(phases.length, phases[0].title)) {
    return phases.map((phase) => {
      const { phaseNo, elements } = phase;
      return (
        <div key={phaseNo}>
          {getActivities({
            touched,
            errors,
            elements,
            ...props,
            phaseNo,
          })}
        </div>
      );
    });
  }

  return phases.map((phase) => {
    const { phaseNo, title, description, elements } = phase;
    return (
      <Fieldset key={phaseNo} title={getPhaseTitle(title, elements, formShowPendingCount)}>
        {getActivities({
          touched,
          errors,
          description,
          elements,
          ...props,
          phaseNo,
        })}
      </Fieldset>
    );
  });
};

export default FormFormat;
